import { useEffect, useState } from "react";

import type { UseMutationResult } from "@tanstack/react-query";
import ThumbsDownIcon from "@uui/ThumbsDown";
import ThumbsUpIcon from "@uui/ThumbsUp";
import { useNavigate } from "react-router-dom";

import Typography from "@mui/material/Typography";

import ReportFeedbackSnackbar, {
  FeedbackSnackbarHeader,
} from "@app/analytics/components/reports/ReportFeedbackSnackbar";
import type { SubmitMyFeedbackBody } from "@app/analytics/MeetingReport/hooks/useMeetingFeedback";
import type { MeetingFeedbackResponse } from "@app/analytics/types";
import useTimeout from "@app/common/hooks/useTimeout";
import { withoutQueryParams } from "@app/common/utils";

import LoadSpinner from "@rui/LoadSpinner";
import { Row } from "@rui/Stack";

import { ContentWrapper, FeedbackButton } from "./styles";

const PendingFeedbackContent = ({
  onClose,
  onSubmitFeedback,
}: {
  onClose: () => void;
  onSubmitFeedback: (response: MeetingFeedbackResponse) => () => void;
}) => (
  <>
    <FeedbackSnackbarHeader
      title="Quick Feedback"
      icon="pending"
      onClose={onClose}
    />
    <Typography variant="bodyPrimaryMed" color="shades.neutral.600">
      Was this meeting a good use of your time?
    </Typography>
    <Row spacing={1.5}>
      <FeedbackButton
        startIcon={<ThumbsUpIcon />}
        onClick={onSubmitFeedback("positive")}
      >
        Yes
      </FeedbackButton>
      <FeedbackButton
        startIcon={<ThumbsDownIcon />}
        onClick={onSubmitFeedback("negative")}
      >
        No
      </FeedbackButton>
    </Row>
  </>
);

const SubmittedFeedbackContent = ({ onClose }: { onClose: () => void }) => (
  <FeedbackSnackbarHeader
    title="Thanks for the feedback!"
    icon="success"
    onClose={onClose}
  />
);

const FeedbackErrorContent = ({ onClose }: { onClose: () => void }) => (
  <>
    <FeedbackSnackbarHeader title="Oops!" icon="error" onClose={onClose} />
    <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
      Something went wrong and your feedback could not be submitted. Please
      refresh the page or try again later.
    </Typography>
  </>
);

const FeedbackSnackbar = ({
  currentResponse,
  onSubmit,
  preselectedResponse,
}: {
  currentResponse?: MeetingFeedbackResponse;
  onSubmit: UseMutationResult<void, Error, SubmitMyFeedbackBody, unknown>;
  preselectedResponse?: MeetingFeedbackResponse;
}) => {
  const navigate = useNavigate();

  const [show, setShow] = useState(
    currentResponse === "pending" || Boolean(preselectedResponse)
  );

  // internal state holding the user's response. this needs to be different from
  // the `currentResponse` prop, since that prop will update right away when
  // feedback is submitted, but we use this prop to determine whether the snackbar
  // is visible (only shows when == "pending") -- and we want a short amount
  // of time between submission and the snackbar disappearing to be able to show
  // the success/error state
  const [feedbackResponse, setFeedbackResponse] =
    useState<MeetingFeedbackResponse | null>(
      preselectedResponse ? "pending" : null
    );
  const [confirmationType, setConfirmationType] = useState<
    "success" | "error" | null
  >(null);

  // reset state after error to allow user to try submitting again
  useTimeout(
    () => setConfirmationType(null),
    5000,
    confirmationType === "error"
  );

  useEffect(() => {
    if (currentResponse != null && feedbackResponse == null) {
      setFeedbackResponse(currentResponse);
    }
  }, [currentResponse]);

  useEffect(() => {
    if (!preselectedResponse) return;
    handleSubmitFeedback(preselectedResponse)();
  }, [preselectedResponse]);

  const handleSubmitFeedback = (response: MeetingFeedbackResponse) => () =>
    onSubmit.mutate(
      { feedback: response },
      {
        onSuccess: () => {
          setConfirmationType("success");
          // short timeout to give time to show the success message
          setTimeout(() => setFeedbackResponse(response), 2000);
          if (preselectedResponse) {
            // remove feedback param once it's been successfully submitted
            navigate(
              withoutQueryParams(location.pathname, location.search, [
                "meetingFeedback",
              ])
            );
          }
        },
        onError: () => setConfirmationType("error"),
      }
    );

  const closeSnackbar = () => {
    setShow(false);
  };

  const handleSkip = () => {
    onSubmit.mutate({ feedback: "skipped" });
    closeSnackbar();
  };

  const snackbarShouldShow = show && feedbackResponse === "pending";
  const handleClose =
    currentResponse === "pending" ? handleSkip : closeSnackbar;

  return (
    <ReportFeedbackSnackbar show={snackbarShouldShow}>
      <ContentWrapper spacing={1.5}>
        {confirmationType === "success" ? (
          <SubmittedFeedbackContent onClose={handleClose} />
        ) : confirmationType === "error" ? (
          <FeedbackErrorContent onClose={handleClose} />
        ) : preselectedResponse ? (
          <LoadSpinner size={32} />
        ) : (
          <PendingFeedbackContent
            onClose={handleClose}
            onSubmitFeedback={handleSubmitFeedback}
          />
        )}
      </ContentWrapper>
    </ReportFeedbackSnackbar>
  );
};

export default FeedbackSnackbar;
